import React, { useState } from 'react';
import { Typography } from 'interfaces/typography';
import { useAutodialerSettings } from '../hooks/useAutodialerSettings';
import { useAutodialerMappings } from '../hooks/useAutodialerMappings';
import { usePendingChanges } from '../hooks/usePendingChanges';
import { SETTING_TO_DISPLAY_NAME } from '../config/settingMaps';
import { Check, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

export function PendingChangesBar() {
    const { 
        hasUnsavedChanges: hasUnsavedSettings, 
        isSaving: isSavingSettings, 
        saveSettings, 
        resetSettings
    } = useAutodialerSettings()
    
    const {
        hasUnsavedChanges: hasUnsavedMappings,
        isSaving: isSavingMappings,
        saveMappings,
        resetMappings
    } = useAutodialerMappings();
    
    const { totalChanges: totalSettingChanges, changes } = usePendingChanges();
    
    const [isExpanded, setIsExpanded] = useState(false);
    
    const hasUnsavedChanges = hasUnsavedSettings || hasUnsavedMappings;
    const isSaving = isSavingSettings || isSavingMappings;
    
    // Calculate total changes (settings + mappings)
    const totalChanges = totalSettingChanges + (hasUnsavedMappings ? 1 : 0);

    const handleSave = async () => {
        if (hasUnsavedSettings) await saveSettings();
        if (hasUnsavedMappings) await saveMappings();
    };

    const handleReset = () => {
        if (hasUnsavedSettings) resetSettings();
        if (hasUnsavedMappings) resetMappings();
    };

    // Format change details for display
    const formatChangeDetails = () => {
        const details: string[] = [];
        
        changes.forEach(change => {
            const settingName = SETTING_TO_DISPLAY_NAME[change.setting] || change.setting.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            
            switch (change.type) {
                case 'added':
                    details.push(`Added ${settingName}`);
                    break;
                case 'modified':
                    details.push(`Modified ${settingName}`);
                    break;
                case 'removed':
                    details.push(`Removed ${settingName}`);
                    break;
            }
        });
        
        if (hasUnsavedMappings) {
            details.push('Modified Automation Mappings');
        }
        
        return details;
    };

    return (
        <div className="w-full">
            {/* Main bar with consistent height */}
            <div className="w-full py-2 flex items-center justify-between min-h-[40px]">
                <div className="flex items-center gap-2">
                    {hasUnsavedChanges ? (
                        <>
                            <AlertCircle size={16} className="text-blue-500" />
                            <Typography variant="mediumParagraph" color="#1e40af" fontWeight="500">
                                {totalChanges} unsaved change{totalChanges !== 1 ? 's' : ''}
                            </Typography>
                            {totalChanges > 0 && (
                                <button
                                    onClick={() => setIsExpanded(!isExpanded)}
                                    className="flex items-center gap-1 px-2 py-1 hover:bg-blue-50 rounded transition-colors"
                                >
                                    <Typography variant="smallParagraph" color="#6b7280" fontWeight="400">
                                        {isExpanded ? 'Hide' : 'Show'} details
                                    </Typography>
                                    {isExpanded ? <ChevronUp size={12} className="text-gray-500" /> : <ChevronDown size={12} className="text-gray-500" />}
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <Check size={16} className="text-green-500" />
                            <Typography variant="mediumParagraph" color="#10b981" fontWeight="500">
                                All changes synced
                            </Typography>
                        </>
                    )}
                </div>
                
                {hasUnsavedChanges && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleReset}
                            className="px-3 py-1 hover:bg-gray-100 rounded transition-colors"
                            style={{
                                fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#6b7280',
                                lineHeight: '18px'
                            }}
                        >
                            Undo
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded transition-colors"
                            style={{
                                fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#ffffff',
                                lineHeight: '18px'
                            }}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                )}
            </div>

            {/* Expandable details section */}
            {isExpanded && hasUnsavedChanges && (
                <div className="bg-blue-50 border-t border-blue-100 px-4 py-3">
                    <Typography variant="smallParagraph" color="#374151" fontWeight="600" className="mb-2">
                        Changed Settings:
                    </Typography>
                    <div className="space-y-1">
                        {formatChangeDetails().map((change, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <Typography variant="smallParagraph" color="#6b7280" fontWeight="400">
                                    {change}
                                </Typography>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
} 